import React, { forwardRef } from 'react';
import { useResponsiveProp } from '../../hooks/use-responsive-prop';
import { styled } from '../../theme';
import type {
  GDSCustomizableComponent,
  GDSResponsiveProp,
  GDSImagePosition,
} from '../../types';
import { CardSection } from './card-section';
import { CardImage } from './card-image';
import { CardClickArea, ImagePositionWrapper } from './card-click-area';
import { GenesisCoreInspector } from '../../test-utils/genesis-core-inspector';

export interface GDSCardProps extends GDSCustomizableComponent {
  children: React.ReactNode;
  imagePosition?: GDSResponsiveProp<GDSImagePosition>;
  backgroundImage?: string;
  backgroundOverlay?: boolean;
  ref?: React.Ref<HTMLDivElement>;
}

export type GDSCardBackgroundColor = 'primary' | 'secondary' | 'highlight';

export interface GDSCardElevatedProps extends GDSCardProps {
  backgroundColor?: GDSCardBackgroundColor;
}

export interface GDSCardFlatProps extends GDSCardProps {
  backgroundColor?: 'transparent' | GDSCardBackgroundColor;
}

// Constants
const FLAT_CARD_DISPLAY_NAME = 'FlatCardContainer';
const ELEVATED_CARD_DISPLAY_NAME = 'ElevatedCard';
const CARD_SECTION_DISPLAY_NAME = 'CardSection';
const CARD_IMAGE_DISPLAY_NAME = 'CardImage';
const CARD_CLICK_AREA_DISPLAY_NAME = 'CardClickArea';

const overlayStyles = {
  backgroundOverlay: {
    true: {
      '&:before': {
        content: '',
        position: 'absolute',
        top: 0,
        insetInlineEnd: 0,
        bottom: 0,
        insetInlineStart: 0,
        width: '100%',
        height: '100%',
        opacity: '40%',
      },
    },
  },
};

const BaseCardContainer = styled(ImagePositionWrapper, {
  position: 'relative',
});

const ElevatedCardBase = styled(BaseCardContainer, {
  border: '$borderWidths$card solid $onSurfaceBorderCard',
  boxShadow: '$card',
  '&:has(.GDS-card-click-area)': {
    border: 'none',
    boxShadow: 'none',
  },
  variants: {
    ...overlayStyles,
    backgroundColor: {
      primary: {
        backgroundColor: '$surfaceCardPrimary',
      },
      secondary: {
        backgroundColor: '$surfaceCardSecondary',
      },
      highlight: {
        backgroundColor: '$surfaceBackgroundTertiary',
      },
    },
  },
  compoundVariants: [
    {
      backgroundColor: 'primary',
      backgroundOverlay: 'true',
      css: {
        '&:before': {
          backgroundColor: '$surfaceCardPrimary',
        },
      },
    },
    {
      backgroundColor: 'secondary',
      backgroundOverlay: 'true',
      css: {
        '&:before': {
          backgroundColor: '$surfaceCardSecondary',
        },
      },
    },
    {
      backgroundColor: 'highlight',
      backgroundOverlay: 'true',
      css: {
        '&:before': {
          backgroundColor: '$surfaceBackgroundTertiary',
        },
      },
    },
  ],
});

const FlatCardBase = styled(BaseCardContainer, {
  variants: {
    ...overlayStyles,
    backgroundColor: {
      transparent: {
        backgroundColor: 'transparent',
      },
      primary: {
        backgroundColor: '$surfaceBackgroundPrimary',
      },
      secondary: {
        backgroundColor: '$surfaceBackgroundSecondary',
      },
      highlight: {
        backgroundColor: '$surfaceBackgroundTertiary',
      },
    },
  },
  compoundVariants: [
    {
      backgroundColor: 'primary',
      backgroundOverlay: 'true',
      css: {
        '&:before': {
          backgroundColor: '$surfaceBackgroundPrimary',
        },
      },
    },
    {
      backgroundColor: 'secondary',
      backgroundOverlay: 'true',
      css: {
        '&:before': {
          backgroundColor: '$surfaceBackgroundSecondary',
        },
      },
    },
    {
      backgroundColor: 'highlight',
      backgroundOverlay: 'true',
      css: {
        '&:before': {
          backgroundColor: '$surfaceBackgroundTertiary',
        },
      },
    },
  ],
});

const ElevatedCardContainer = forwardRef<HTMLDivElement, GDSCardElevatedProps>(
  (
    {
      backgroundColor = 'primary',
      backgroundImage,
      backgroundOverlay,
      children,
      className,
      css,
      imagePosition = 'top',
      'data-testid': testId = `GDS-${ELEVATED_CARD_DISPLAY_NAME}`,
      ...props
    }: GDSCardElevatedProps,
    ref,
  ) => {
    const cardImagePosition = useResponsiveProp(imagePosition);
    return (
      <GenesisCoreInspector displayName={ELEVATED_CARD_DISPLAY_NAME}>
        <ElevatedCardBase
          data-testid={testId}
          backgroundColor={backgroundColor}
          backgroundOverlay={backgroundOverlay}
          className={['GDS-elevated-card', className].join(' ')}
          css={{
            ...css,
            ...(backgroundImage && {
              backgroundImage: `url(${backgroundImage})`,
              backgroundRepeat: 'no-repeat',
              backgroundSize: 'cover',
              backgroundPosition: 'center',
            }),
          }}
          imagePosition={cardImagePosition}
          ref={ref}
          {...props}
        >
          {children}
        </ElevatedCardBase>
      </GenesisCoreInspector>
    );
  },
);

const FlatCardContainer = forwardRef<HTMLDivElement, GDSCardFlatProps>(
  (
    {
      backgroundColor = 'primary',
      backgroundImage,
      backgroundOverlay,
      children,
      css,
      className,
      imagePosition = 'top',
      'data-testid': testId = `GDS-${FLAT_CARD_DISPLAY_NAME}`,
      ...props
    }: GDSCardFlatProps,
    ref,
  ) => {
    const cardImagePosition = useResponsiveProp(imagePosition);
    return (
      <GenesisCoreInspector displayName={FLAT_CARD_DISPLAY_NAME}>
        <FlatCardBase
          data-testid={testId}
          backgroundColor={backgroundColor}
          backgroundOverlay={backgroundOverlay}
          className={['GDS-flat-card', className].join(' ')}
          css={{
            ...css,
            ...(backgroundImage && {
              backgroundImage: `url(${backgroundImage})`,
              backgroundRepeat: 'no-repeat',
              backgroundSize: 'cover',
              backgroundPosition: 'center',
            }),
          }}
          imagePosition={cardImagePosition}
          ref={ref}
          {...props}
        >
          {children}
        </FlatCardBase>
      </GenesisCoreInspector>
    );
  },
);

export const Card = {
  Flat: FlatCardContainer,
  Elevated: ElevatedCardContainer,
  Section: CardSection,
  Image: CardImage,
  ClickArea: CardClickArea,
};

Card.Flat.displayName = FLAT_CARD_DISPLAY_NAME;
Card.Elevated.displayName = ELEVATED_CARD_DISPLAY_NAME;
Card.Section.displayName = CARD_SECTION_DISPLAY_NAME;
Card.Image.displayName = CARD_IMAGE_DISPLAY_NAME;
Card.ClickArea.displayName = CARD_CLICK_AREA_DISPLAY_NAME;
